/**
 * GmOrchestratorServiceModule — V3 Voice GM (2026-06-02).
 *
 * Wraps the briefing-room backend's
 * POST /api/modules/briefing-room/dispatch with Bearer auth.
 *
 * Backend contract: routes/dispatch.py:DispatchRequest -> DispatchResponse.
 * The route mounts under vibe-designer's AuthMiddleware → fetch needs
 * the Authorization header (briefing-room fetch rule). The dispatch
 * server-side calls the per-tenant LLM router; failures surface as
 * 503 (unconfigured / unreachable), 504 (timeout), 502 (upstream
 * malformed). GmOrchestratorError carries the discrete code so the
 * UI can show actionable messaging.
 */
import {
  GmOrchestratorError,
  type DispatchRequest,
  type DispatchResponse,
  type GmOrchestratorService,
} from '../types';
import { getAuthHeaders } from './authHeaders';

const DISPATCH_PATH = '/api/modules/briefing-room/dispatch';

interface ErrorDetail {
  detail?: { code?: unknown; message?: unknown };
}

export class GmOrchestratorServiceModule implements GmOrchestratorService {
  constructor(
    private readonly dispatchPath: string = DISPATCH_PATH,
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  isAvailable(): boolean {
    return true;
  }

  async dispatch(req: DispatchRequest): Promise<DispatchResponse> {
    let resp: Response;
    try {
      resp = await this.fetchFn(this.dispatchPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(req),
      });
    } catch (err: unknown) {
      throw new GmOrchestratorError(
        'network',
        0,
        err instanceof Error ? err.message : String(err),
      );
    }
    if (!resp.ok) {
      let code = 'http_error';
      let message = `HTTP ${resp.status}`;
      try {
        const body = (await resp.json()) as ErrorDetail;
        const detail = body.detail;
        if (typeof detail?.code === 'string') {
          code = detail.code;
        }
        if (typeof detail?.message === 'string') {
          message = detail.message;
        }
      } catch {
        // body wasn't JSON — keep the default code/message.
      }
      throw new GmOrchestratorError(code, resp.status, message);
    }
    return (await resp.json()) as DispatchResponse;
  }
}
